/* -------------------------------------------------------------------------- */
/*  Fortis Construction – Viewpoint Vista job-cost import                       */
/* -------------------------------------------------------------------------- *
 *  Record types:                                                              *
 *    2 = Phase (one per full phase code, named after its Phase Code 1)         *
 *    3 = Cost type (one per phase + cost type, amounts rolled up)              *
 *  - Full phase code is "Phase Code 1-Phase Code 2-Phase Code 3"              *
 *  - Line items only; each LMSEO bucket carries its own cost type             *
 *  - Comma-delimited, text fields quoted                                      *
 *  - Returns [stringLines, "txt"]                                             */
/* -------------------------------------------------------------------------- */

import { ValidationError } from "../../errors";
import { CategoryKey, nonZeroCategories } from "./categories";
import { BudgetImportConfig, BudgetRow } from "./framework/types";
import { assertDetailTotalMatches, stripLeadingZeros, truncateList } from "./framework/helpers";

/* ───────────────────────── 1. Constants ─────────────────────────────────── */

/** Phase Code 2 and 3 are optional on a line item, but Vista needs every segment filled. */
const PHASE_CODE_2_DEFAULT = "00";
const PHASE_CODE_3_DEFAULT = "0000";

/** Vista's labor cost type; its "3" record carries a 1.0 in the hours column. */
const LABOR_COST_TYPE = "1";

/**
 * Each LMSEO bucket's cost type comes from its own sort field, falling back to Fortis's standard
 * Vista cost type for that bucket. "Other" (and a line item with no LMSEO split at all) takes the
 * line item's Cost Type.
 */
const COST_TYPE_FIELDS: Record<CategoryKey, { field: string; fallback: string }> = {
  labor: { field: "Labor Cost Type Category_code", fallback: "01" },
  material: { field: "Material Cost Type Category_code", fallback: "02" },
  equip: { field: "Equipment Cost Type Category_code", fallback: "03" },
  sub: { field: "Subcontractor Cost Type Category_code", fallback: "05" },
  other: { field: "Cost Type_code", fallback: "04" },
  total: { field: "Cost Type_code", fallback: "04" },
};

/* ───────────────────────── 2. Types ─────────────────────────────────────── */

/** One phase + cost type combination, with every line item's share summed in. */
interface CostTypeTotal {
  phaseCode: string;
  costType: string;
  amount: number;
}

/* ───────────────────────── 3. Helpers ───────────────────────────────────── */

const fullPhaseCode = (row: BudgetRow): string =>
  [
    row["Phase Code 1_code"],
    row["Phase Code 2_code"] || PHASE_CODE_2_DEFAULT,
    row["Phase Code 3_code"] || PHASE_CODE_3_DEFAULT,
  ].join("-");

// The description is the file's only free-text field. Its commas become pipes, as in the
// proof-of-concept workbook, rather than trusting Vista's importer to honor the quotes; a quote
// inside it (an inch mark, say) is doubled so it cannot close the field early.
const phaseDescription = (row: BudgetRow): string =>
  ((row["Phase Code 1_desc"] as string) || "").replace(/,/g, "|").replace(/"/g, '""');

/** Vista reads cost types as numbers, so the sort code "05" is written as "5". */
const costTypeOf = (row: BudgetRow, bucket: CategoryKey): string => {
  const { field, fallback } = COST_TYPE_FIELDS[bucket];
  return stripLeadingZeros(row[field] || fallback);
};

const compareCostTypes = (a: string, b: string): number => {
  const [numA, numB] = [Number(a), Number(b)];
  return Number.isNaN(numA) || Number.isNaN(numB) ? a.localeCompare(b) : numA - numB;
};

const compareCostTypeTotals = (a: CostTypeTotal, b: CostTypeTotal): number =>
  a.phaseCode.localeCompare(b.phaseCode) || compareCostTypes(a.costType, b.costType);

/* ───────────────────────── 4. Record builders ────────────────────────────── */

const buildPhaseRecord = (phaseCode: string, description: string): string =>
  `"2","","","${phaseCode}","${description}","",,,,`;

const buildCostTypeRecord = ({ phaseCode, costType, amount }: CostTypeTotal): string => {
  const hours = costType === LABOR_COST_TYPE ? "1.0" : "";
  return `"3","","","${phaseCode}","${costType}",1.0,"LS",${hours},${amount.toFixed(2)},"","","",`;
};

/* ───────────────────────── 5. Config ─────────────────────────────────────── */

export const fortisConfig: BudgetImportConfig = {
  format: "vista",
  vista: {
    ext: "txt",
    render: (ctx) => {
      const lineItems = ctx.rows.filter((row) => row.type === "line_item");
      if (lineItems.length === 0) {
        throw new ValidationError("No line items found in the estimate. Load estimate data before exporting.");
      }

      const missingPhaseCode = lineItems
        .filter((row) => !row["Phase Code 1_code"])
        .map((row) => row.name || "(unnamed item)");
      if (missingPhaseCode.length > 0) {
        throw new ValidationError(
          `Missing Phase Code 1 for: ${truncateList(missingPhaseCode)}. Assign a Phase Code 1 to all line items before exporting.`
        );
      }

      /* Roll every line item's LMSEO buckets up into phase + cost type totals */
      const phaseDescriptions = new Map<string, string>();
      const totals = new Map<string, CostTypeTotal>();
      for (const row of lineItems) {
        const phaseCode = fullPhaseCode(row);
        const quantity: number = row.quantity ?? 0;
        for (const { key, unitCost } of nonZeroCategories(row)) {
          const amount = quantity * (unitCost ?? 0);
          if (Math.abs(amount) < 0.005) continue;
          const costType = costTypeOf(row, key);
          const existing = totals.get(`${phaseCode}|${costType}`);
          if (existing) {
            existing.amount += amount;
          } else {
            totals.set(`${phaseCode}|${costType}`, { phaseCode, costType, amount });
          }
          if (!phaseDescriptions.has(phaseCode)) phaseDescriptions.set(phaseCode, phaseDescription(row));
        }
      }

      const costTypeTotals = Array.from(totals.values());
      assertDetailTotalMatches(
        lineItems,
        costTypeTotals.reduce((sum, total) => sum + total.amount, 0)
      );

      /* Assemble output: every phase first, then the cost totals under them */
      const phaseRecords = Array.from(phaseDescriptions.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([phaseCode, description]) => buildPhaseRecord(phaseCode, description));

      const costTypeRecords = costTypeTotals
        .filter(({ amount }) => Math.abs(amount) >= 0.005)
        .sort(compareCostTypeTotals)
        .map(buildCostTypeRecord);

      return [...phaseRecords, ...costTypeRecords];
    },
  },
};
